import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { PaymentReceiptTypeUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CurrencyCreate } from "../../../currency/components/create/CurrencyCreate";
import { CurrencyCreateStore } from "../../../currency/store";
import { FormControl } from "@mui/material";

export type PaymentReceiptTypeUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_es: string;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_esError?: string;
  name_en: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_enError?: string;
  name_pt: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_ptError?: string;
  price: number;
  priceSelectedValue?: { displayId: string; id: string } | null;
  onPriceChange?: (value: number) => void;
  priceError?: string;
  currencySelectedValue?: { displayId: string; id: string } | null;
  onCurrencyChange?: (value: string) => void;
  onCurrencySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  currencyOptions?: { displayId: string; id: string }[];
  currencyError?: string;
  paymentReceiptTypeUpdateStore: PaymentReceiptTypeUpdateStore;
};

export const PaymentReceiptTypeUpdateForm: React.FC<PaymentReceiptTypeUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name_es = "",
      name_esError,
      onName_esChange = () => {},
      name_en = "",
      name_enError,
      onName_enChange = () => {},
      name_pt = "",
      name_ptError,
      onName_ptChange = () => {},
      price = 0,
      priceError,
      onPriceChange = () => {},
      onCurrencySelected = () => {},
      currencyOptions = [],
      currencySelectedValue = null,
      currencyError,
      onCurrencyChange = () => {},
      paymentReceiptTypeUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="PaymentReceiptType"
              mode="update"
              values={{}}
              options={{ currency: currencyOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name_es":
                    onName_esChange?.(valor);
                    break;
                  case "name_en":
                    onName_enChange?.(valor);
                    break;
                  case "name_pt":
                    onName_ptChange?.(valor);
                    break;
                  case "price":
                    onPriceChange?.(valor);
                    break;
                  case "currency":
                    onCurrencySelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={paymentReceiptTypeUpdateStore.getCardErrors("null")}
              title={langStore.groupPaymentReceiptTypeLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name_es}
                  error={Boolean(name_esError)}
                  warning={
                    !name_esError &&
                    Boolean(paymentReceiptTypeUpdateStore.warnings["name_es"])
                  }
                  helperText={
                    name_esError ||
                    paymentReceiptTypeUpdateStore.warnings["name_es"]?.message
                  }
                  label={langStore.PaymentReceiptTypeNameLabel + " (es)"}
                  onChange={(value: string) => onName_esChange(value)}
                  description={langStore.PaymentReceiptTypeNameDescription}
                />
                <InputString
                  value={name_en}
                  error={Boolean(name_enError)}
                  warning={
                    !name_enError &&
                    Boolean(paymentReceiptTypeUpdateStore.warnings["name_en"])
                  }
                  helperText={
                    name_enError ||
                    paymentReceiptTypeUpdateStore.warnings["name_en"]?.message
                  }
                  label={langStore.PaymentReceiptTypeNameLabel + " (en)"}
                  onChange={(value: string) => onName_enChange(value)}
                  description={langStore.PaymentReceiptTypeNameDescription}
                />
                <InputString
                  value={name_pt}
                  error={Boolean(name_ptError)}
                  warning={
                    !name_ptError &&
                    Boolean(paymentReceiptTypeUpdateStore.warnings["name_pt"])
                  }
                  helperText={
                    name_ptError ||
                    paymentReceiptTypeUpdateStore.warnings["name_pt"]?.message
                  }
                  label={langStore.PaymentReceiptTypeNameLabel + " (pt)"}
                  onChange={(value: string) => onName_ptChange(value)}
                  description={langStore.PaymentReceiptTypeNameDescription}
                />
                <InputNumber
                  value={price}
                  error={Boolean(priceError)}
                  warning={
                    !priceError &&
                    Boolean(paymentReceiptTypeUpdateStore.warnings["price"])
                  }
                  helperText={
                    priceError ||
                    paymentReceiptTypeUpdateStore.warnings["price"]?.message
                  }
                  label={langStore.PaymentReceiptTypePriceLabel}
                  decimalScale={2}
                  description={langStore.PaymentReceiptTypePriceDescription}
                  onChange={(value: number) => onPriceChange(value)}
                />
                <InputReference
                  id="currency"
                  label={langStore.PaymentReceiptTypeCurrencyLabel}
                  error={Boolean(currencyError)}
                  helperText={
                    currencyError ||
                    paymentReceiptTypeUpdateStore.warnings["currency"]?.message
                  }
                  selectedValue={currencySelectedValue}
                  onChange={(value: string) => onCurrencyChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCurrencySelected(value)
                  }
                  options={currencyOptions}
                  description={langStore.PaymentReceiptTypeCurrencyDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "currency_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CurrencyCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CurrencyCreate
                          currencyCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCurrency"
                      cancelMethod="cancelNewCurrency"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
